import * as React from "react";
import { Outlet } from "react-router-dom";

import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { MobileNavDrawer } from "@/components/layout/MobileNavDrawer";
import { Banner } from "@/components/ui/banner";
import { LapseNotices } from "@/components/billing/LapseNotices";
import { OfflineBanner } from "@/components/pwa/OfflineBanner";
import { UpdateBanner } from "@/components/pwa/UpdateBanner";
import { useAuth } from "@/contexts/auth-context";

// Story 16.2 / Franklin#445 — canonical persistent-banner copy for a gated legacy
// clinician. Two stages, both still gated (is_active=false), distinguished by
// cert_verified so the copy is accurate at each step:
//   stage-1 (cert pending) — verification is the next action.
//   stage-2 (cert approved, Franklin#445) — verification is DONE; the account
//     stays locked pending activation. The earlier single string wrongly told an
//     approved clinician their cert was still unverified.
export const LEGACY_GATED_BANNER_PENDING_CERT =
  "Your account is disabled until your AMPS certificate is verified.";
export const LEGACY_GATED_BANNER_APPROVED_LOCKED =
  "Your AMPS certificate is approved. Your account stays locked until activation is complete.";

interface AppShellProps {
  /** Optional content; defaults to the routed <Outlet/> so it works as a layout route. */
  children?: React.ReactNode;
}

// The stable frame every authenticated screen renders inside (UX Screen 02).
// Landmarks: <header> (TopBar), <aside> (Sidebar), <main> (content).
export function AppShell({ children }: AppShellProps) {
  const [navOpen, setNavOpen] = React.useState(false);
  const { user, isLegacyGated } = useAuth();
  // Franklin#445 — both stages are gated; cert_verified picks the accurate copy.
  const bannerCopy = user?.certVerified
    ? LEGACY_GATED_BANNER_APPROVED_LOCKED
    : LEGACY_GATED_BANNER_PENDING_CERT;

  return (
    <div className="min-h-screen">
      {/* Story 7.7 (UX-DR17) — full-bleed offline strip at the viewport top. Returns null when
          online, so it adds no layout when connected; when offline it sits ABOVE the card and
          never overlaps the in-card lapsed-subscription banner. */}
      <OfflineBanner />
      <div className="mx-auto w-[min(var(--max-width),calc(100vw-40px))] py-7">
        <div className="rounded-[var(--radius-3xl)] border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-5 shadow-elevated sm:p-7">
          <TopBar onOpenNav={() => setNavOpen(true)} />
          {/* Story 16.2 — persistent gate banner on EVERY in-shell page for a
              gated legacy clinician (variant="warn" → role="alert"). */}
          {isLegacyGated ? (
            <Banner variant="warn" className="mt-5">
              {bannerCopy}
            </Banner>
          ) : null}
          {/* #420 — the Story 7.5 opt-in "new version available — reload" prompt.
              Mounted here so it surfaces on EVERY in-shell page (incl. /profile)
              after a deploy; without this mount the SW's deferred no-skipWaiting
              kept serving the stale app shell with no reload prompt. The eval
              scoring stepper is shell-less, so it is structurally excluded — an
              in-progress evaluation is never interrupted. Renders null until the
              SW signals a waiting update. */}
          <UpdateBanner className="mt-5" />
          {/* Story 10.5 (UX-DR16) — countdown/lapsed banner or day-zero modal on EVERY
              in-shell page for a near-lapse/expired B2C clinician. In-card, matching the
              Screen-02 lapsed-banner position. Renders null when healthy. The shell-less
              eval stepper mounts its own copy (gh-460 dual-mount precedent). */}
          <LapseNotices className="mt-5" />
          <div className="mt-5 flex gap-5">
            <Sidebar />
            {/* tabIndex: stable focus target when a notice above dismisses itself. */}
            <main tabIndex={-1} className="min-w-0 flex-1 outline-none">
              {children ?? <Outlet />}
            </main>
          </div>
        </div>
      </div>
      <MobileNavDrawer open={navOpen} onOpenChange={setNavOpen} />
    </div>
  );
}
